import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, Activity, 
  CheckCircle, XCircle, AlertTriangle, Clock, Zap, Target,
  BarChart3, PieChart, Calendar, Users, MessageCircle, Phone, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component
const StatCard = ({ title, value, change, changeType, icon: Icon, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    green: "bg-green-100 text-green-600",
    red: "bg-red-100 text-red-600",
    amber: "bg-amber-100 text-amber-600",
    purple: "bg-purple-100 text-purple-600"
  };
  
  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {change !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {changeType === 'up' ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : changeType === 'down' ? (
                  <TrendingDown className="h-4 w-4 text-red-600" />
                ) : null}
                <span className={`text-sm ${changeType === 'up' ? 'text-green-600' : changeType === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                  {change}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, total, color = "blue" }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    red: "bg-red-500",
    amber: "bg-amber-500",
    purple: "bg-purple-500"
  };
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-700">{label}</span>
        <span className="text-slate-500">{value} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Trigger icons mapping
const triggerIcons = {
  contact_created: Users,
  lead_created: Target,
  lead_updated: Target,
  deal_created: Zap,
  deal_stage_changed: Activity,
  deal_won: CheckCircle,
  call_completed: Phone,
  whatsapp_received: MessageCircle,
  form_submitted: Mail,
  booking_created: Calendar,
  task_completed: CheckCircle,
  scheduled: Clock
};

// Main Component
const WorkflowAnalytics = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalExecutions: 0,
    successfulExecutions: 0,
    failedExecutions: 0,
    skippedExecutions: 0,
    activeWorkflows: 0,
    totalWorkflows: 0,
    executionsByTrigger: {},
    executionsByWorkflow: [],
    executionsByDay: [],
    actionStats: {}
  });
  
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Fetch workflows
      const workflowsRes = await fetch(
        `${API_URL}/api/workflow-builder/workflows?token=${token}`
      );
      const workflowsData = await workflowsRes.json();
      const workflows = workflowsData.workflows || [];
      
      // Fetch organizations if super admin
      if (isSuperAdmin && organizations.length === 0) {
        const orgsRes = await fetch(`${API_URL}/api/workflow-builder/organizations?token=${token}`);
        const orgsData = await orgsRes.json();
        setOrganizations(orgsData.organizations || []);
      }
      
      // Fetch history for all workflows
      const allHistory = [];
      for (const workflow of workflows.slice(0, 20)) {
        try {
          const historyRes = await fetch(
            `${API_URL}/api/workflow-builder/workflows/${workflow.id}/history?token=${token}&limit=100`
          );
          const historyData = await historyRes.json();
          if (historyData.success && historyData.history) {
            allHistory.push(...historyData.history.map(h => ({
              ...h,
              workflow_name: workflow.name,
              workflow_id: workflow.id
            })));
          }
        } catch (e) {
          console.error(`Failed to fetch history for ${workflow.id}:`, e);
        }
      }
      
      // Filter by time range
      const now = new Date();
      const rangeMs = {
        '24h': 24 * 60 * 60 * 1000,
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
        '90d': 90 * 24 * 60 * 60 * 1000
      };
      
      const filteredHistory = allHistory.filter(h => {
        const execDate = new Date(h.executed_at);
        const inRange = (now - execDate) <= rangeMs[timeRange];
        const matchesOrg = selectedOrg === 'all' || h.organization_id === selectedOrg;
        return inRange && matchesOrg;
      });
      
      // Calculate stats
      const successful = filteredHistory.filter(h => 
        h.result?.success === true && h.result?.conditions_met !== false
      ).length;
      
      const failed = filteredHistory.filter(h => 
        h.result?.success === false || h.result?.errors?.length > 0
      ).length;
      
      const skipped = filteredHistory.filter(h => 
        h.result?.conditions_met === false
      ).length;
      
      // Executions by trigger
      const byTrigger = {};
      filteredHistory.forEach(h => {
        const trigger = h.trigger || 'unknown';
        byTrigger[trigger] = (byTrigger[trigger] || 0) + 1;
      });
      
      // Executions by workflow
      const byWorkflow = {};
      filteredHistory.forEach(h => {
        const name = h.workflow_name || 'Unknown';
        if (!byWorkflow[name]) {
          byWorkflow[name] = { name, total: 0, success: 0, failed: 0 };
        }
        byWorkflow[name].total++;
        if (h.result?.success && h.result?.conditions_met !== false) {
          byWorkflow[name].success++;
        } else if (h.result?.success === false || h.result?.errors?.length > 0) {
          byWorkflow[name].failed++;
        }
      });
      
      // Executions by day
      const byDay = {};
      filteredHistory.forEach(h => {
        const day = new Date(h.executed_at).toLocaleDateString('en-US', { 
          timeZone: 'Asia/Dubai',
          month: 'short', 
          day: 'numeric' 
        });
        byDay[day] = (byDay[day] || 0) + 1;
      });
      
      // Action stats
      const actionStats = {};
      filteredHistory.forEach(h => {
        const actions = h.result?.actions_executed || [];
        actions.forEach(a => {
          const actionType = a.action || 'unknown';
          if (!actionStats[actionType]) {
            actionStats[actionType] = { total: 0, success: 0, failed: 0 };
          }
          actionStats[actionType].total++;
          if (a.success) {
            actionStats[actionType].success++;
          } else {
            actionStats[actionType].failed++;
          }
        });
      });
      
      // Active workflows
      const activeWorkflows = workflows.filter(w => w.is_active).length;
      
      setAnalytics({
        totalExecutions: filteredHistory.length,
        successfulExecutions: successful,
        failedExecutions: failed,
        skippedExecutions: skipped,
        activeWorkflows,
        totalWorkflows: workflows.length,
        executionsByTrigger: byTrigger,
        executionsByWorkflow: Object.values(byWorkflow).sort((a, b) => b.total - a.total),
        executionsByDay: Object.entries(byDay).map(([date, count]) => ({ date, count })),
        actionStats
      });
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [token, timeRange, selectedOrg, isSuperAdmin, organizations.length]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  const successRate = analytics.totalExecutions > 0 
    ? ((analytics.successfulExecutions / analytics.totalExecutions) * 100).toFixed(1)
    : 0;
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/workflows-builder')}
                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Workflow Analytics
                </h1>
                <p className="text-sm text-slate-500">Execution statistics and performance metrics</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-[130px] bg-white border-slate-200" data-testid="time-range-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="24h">Last 24 hours</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              
              {isSuperAdmin && (
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger className="w-[180px] bg-white border-slate-200" data-testid="org-filter">
                    <SelectValue placeholder="All Organizations" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAnalytics}
                disabled={refreshing}
                className="border-slate-200 text-slate-700 hover:bg-slate-100"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                title="Total Executions"
                value={analytics.totalExecutions}
                icon={Activity}
                color="blue"
              />
              <StatCard
                title="Success Rate"
                value={`${successRate}%`}
                icon={CheckCircle}
                color="green"
              />
              <StatCard
                title="Failed Executions"
                value={analytics.failedExecutions}
                icon={XCircle}
                color="red"
              />
              <StatCard
                title="Active Workflows"
                value={`${analytics.activeWorkflows}/${analytics.totalWorkflows}`}
                icon={Zap}
                color="purple"
              />
            </div>
            
            {/* Execution Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Status */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-blue-600" />
                    Execution Status Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ProgressBar 
                    label="Successful" 
                    value={analytics.successfulExecutions} 
                    total={analytics.totalExecutions}
                    color="green"
                  />
                  <ProgressBar 
                    label="Skipped (Conditions not met)" 
                    value={analytics.skippedExecutions} 
                    total={analytics.totalExecutions}
                    color="amber"
                  />
                  <ProgressBar 
                    label="Failed" 
                    value={analytics.failedExecutions} 
                    total={analytics.totalExecutions}
                    color="red"
                  />
                </CardContent>
              </Card>
              
              {/* By Trigger */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-500" />
                    Executions by Trigger
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.keys(analytics.executionsByTrigger).length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No executions in this period</p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(analytics.executionsByTrigger)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 6)
                        .map(([trigger, count]) => {
                          const TriggerIcon = triggerIcons[trigger] || Zap;
                          return (
                            <div key={trigger} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <TriggerIcon className="h-4 w-4 text-slate-500" />
                                <span className="text-slate-700 capitalize">
                                  {trigger.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <Badge variant="outline" className="border-slate-300">{count}</Badge>
                            </div>
                          );
                        })}
                    </div>
                  )}}
                </CardContent>
              </Card>
            </div>
            
            {/* Top Workflows */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-600" />
                  Top Performing Workflows
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Workflows ranked by execution count
                </CardDescription>
              </CardHeader>
              <CardContent>
                {analytics.executionsByWorkflow.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No workflow executions in this period</p>
                ) : (
                  <div className="space-y-4">
                    {analytics.executionsByWorkflow.slice(0, 5).map((workflow, idx) => {
                      const successPct = workflow.total > 0 
                        ? ((workflow.success / workflow.total) * 100).toFixed(0)
                        : 0;
                      return (
                        <div key={workflow.name} className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-slate-900 font-medium">{workflow.name}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-slate-500">{workflow.total} executions</span>
                              <span className="text-green-600">{workflow.success} success</span>
                              {workflow.failed > 0 && (
                                <span className="text-red-600">{workflow.failed} failed</span>
                              )}
                            </div>
                          </div>
                          <Badge 
                            className={successPct >= 80 ? 'bg-green-100 text-green-700' : 
                                      successPct >= 50 ? 'bg-amber-100 text-amber-700' : 
                                      'bg-red-100 text-red-700'}
                          >
                            {successPct}% success
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Action Performance */}
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-600" />
                  Action Performance
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Success rates for different action types
                </CardDescription>
              </CardHeader>
              <CardContent>
                {Object.keys(analytics.actionStats).length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No actions executed in this period</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(analytics.actionStats)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([action, stats]) => {
                        const successPct = stats.total > 0 
                          ? ((stats.success / stats.total) * 100).toFixed(0)
                          : 0;
                        return (
                          <div key={action} className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                            <p className="text-slate-900 font-medium capitalize mb-2">
                              {action.replace(/_/g, ' ')}
                            </p>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-slate-500">{stats.total} total</span>
                              <Badge 
                                className={successPct >= 80 ? 'bg-green-100 text-green-700' : 
                                          successPct >= 50 ? 'bg-amber-100 text-amber-700' : 
                                          'bg-red-100 text-red-700'}
                              >
                                {successPct}% success
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default WorkflowAnalytics;
